import React, { FC, useEffect, useState } from 'react';
import { Modal, Select, Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import styled from 'styled-components';

const { Option } = Select;

const currentYear = new Date().getFullYear();

const ScrollableBody = styled.div`
    height: calc(100vh - 200px);
    overflow-y: auto;
    padding-right: 16px;
`;

interface UploaderFilterModalProps {
    uploaderFilterModalOpen: boolean;
    handleCancel: () => void;
    handleSetFilters: () => void;
    showTerritory: boolean;
    showMonth: boolean;
    showYear: boolean;
    platformOptions: { [key: string]: string[] };
    territoryOptions: string[];
    form: FormInstance;
    handleFormChange: () => void;
    domain: string;
    platformKey: string;
    channelOptions: string[];
    showChannel: boolean;
    showHOVField: boolean;
    showViewershipPartner: boolean;
    borrowerPartnerOptions: string[];
    loading: boolean;
}

const UploaderFilterModal: FC<UploaderFilterModalProps> = ({
    uploaderFilterModalOpen,
    handleCancel,
    handleSetFilters,
    showTerritory,
    showMonth,
    showYear,
    platformOptions,
    territoryOptions,
    form,
    handleFormChange,
    platformKey,
    channelOptions,
    showChannel,
    showHOVField,
    domain,
    showViewershipPartner,
    borrowerPartnerOptions,
    loading,
}) => {
    const [quarter, setQuarter] = useState('');
    const [month, setMonth] = useState();

    useEffect(() => {
        const quarter = form.getFieldValue("quarter");
        setQuarter(quarter);
    }, [])
    return (
        <Modal
            centered
            title="Uploader Filter"
            open={uploaderFilterModalOpen}
            okText={showViewershipPartner ? "Insert Data" : "Set"}
            onCancel={handleCancel}
            onOk={handleSetFilters}
            maskClosable={false}
            confirmLoading={loading}
            // style={{ top: 24 }}
        >
            <ScrollableBody>
                <Form form={form} layout="vertical" disabled={loading}>
                    <Form.Item
                        label="Borrowed"
                        name="is_borrowed"
                        rules={[{ required: true, message: 'Please select borrowed type' }]}
                        initialValue="direct"
                    >
                        <Select placeholder="Select Borrowed" onChange={() => {
                            form.resetFields(["platform", "partner", "channel", "territory", "year", "month", "quarter", "hov", "lender"]);
                            handleFormChange();
                        }}>
                            <Option value="direct">Direct</Option>
                            <Option value="borrowed">Borrowed</Option>
                        </Select>
                    </Form.Item>
                    
                    <Form.Item
                        label="Domain"
                        name="domain"
                        rules={[{ required: true, message: 'Please select domain' }]}
                    >
                        <Select placeholder="Select Domain" onChange={handleFormChange}>
                            <Option value="Distribution Partners">Distribution Partners</Option>
                            {!showViewershipPartner && <Option value="Owned and Operated" disabled>Owned and Operated (Coming Soon...)</Option>}
                        </Select>
                    </Form.Item>
                    {
                        !showChannel && (
                            <Form.Item
                                label="Platform"
                                name="platform"
                                rules={[{ required: true, message: 'Please select platform' }]}
                            >
                                <Select placeholder="Select Platform" onChange={handleFormChange}>
                                    {platformOptions[platformKey]?.map(platform => (
                                        <Option key={platform} value={platform}>{platform}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )
                    }
                    {showViewershipPartner && (
                        <Form.Item
                            label="Borrower"
                            name="partner"
                            rules={[{ required: true, message: 'Please select partner' }]}
                        >
                            <Select placeholder="Select Borrower Partner" onChange={(value) => {
                                form.resetFields(["territory", "channel", "platform"])
                                handleFormChange();
                            }}>
                                {borrowerPartnerOptions.map((borrowerPartner: string) => (
                                    <Option key={borrowerPartner} value={borrowerPartner}>{borrowerPartner}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {showTerritory && (
                        <Form.Item
                            label="Territory (Unspecified is considered as United States usually)"
                            name="territory"
                            rules={[{ required: true, message: 'Please select territory' }]}
                        >
                            <Select placeholder="Select Territory" onChange={(value) => {
                                // Do not reset platform when territory changes
                                form.resetFields(["channel", "lender"])
                                handleFormChange();
                            }}>
                                {territoryOptions?.map((territory: string) => (
                                    <Option key={territory} value={territory}>{territory}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {showChannel && (
                        <Form.Item
                            label="Channel (Unspecified is considered as Nosey usually)"
                            name="channel"
                            rules={[{ required: true, message: 'Please select channel' }]}
                        >   
                            <Select placeholder="Select Channel" onChange={(value) => {
                                form.resetFields(["platform"])
                                handleFormChange();
                            }}>
                                {channelOptions?.map((channel: string) => (
                                    <Option key={channel} value={channel}>{channel}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {showChannel && (
                        <Form.Item
                            label="Lender"
                            name="lender"
                            rules={[{ required: true, message: 'Please select a lender' }]}
                        >
                            <Select placeholder="Select Platform" onChange={handleFormChange}>
                                {platformOptions[platformKey]?.map(platform => (
                                    <Option key={platform} value={platform}>{platform}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {showYear && (
                        <Form.Item
                            label="Year"
                            name="year"
                            rules={[{ required: true, message: 'Please select year' }]}
                        >
                            <Select placeholder="Select Year" onChange={handleFormChange}>
                                <Option value={String(currentYear-1)}>{currentYear-1}</Option>
                                <Option value={String(currentYear)}>{currentYear}</Option>
                            </Select>
                        </Form.Item>
                    )}
                    {showYear && (
                        <Form.Item
                            label="Quarter"
                            name="quarter"
                            rules={[{ required: true, message: 'Please select quarter' }]}
                        >
                            <Select placeholder="Select Quarter" onChange={(value) => {
                                // handleFormChange();
                                setQuarter(value as string);
                            }}>
                                <Option value="q1">Q1</Option>
                                <Option value="q2">Q2</Option>
                                <Option value="q3">Q3</Option>
                                <Option value="q4">Q4</Option>
                            </Select>
                        </Form.Item>
                    )}
                    {showMonth && (
                        <Form.Item
                            label="Month"
                            name="month"
                            rules={[{ required: true, message: 'Please select month' }]}
                        >
                            <Select placeholder="Select Month" onChange={(value) => {
                                // handleFormChange();
                                setMonth(value);
                            }}>
                                {quarter === 'q1' && (
                                    <>
                                    <Option value="1">January</Option>
                                    <Option value="2">February</Option>
                                    <Option value="3">March</Option>
                                    </>
                                )}
                                {quarter === 'q2' && (
                                    <>
                                    <Option value="4">April</Option>
                                    <Option value="5">May</Option>
                                    <Option value="6">June</Option>
                                    </>
                                )}
                                {quarter === 'q3' && (
                                    <>
                                    <Option value="7">July</Option>
                                    <Option value="8">August</Option>
                                    <Option value="9">September</Option>
                                    </>
                                )}
                                {quarter === 'q4' && (
                                    <>
                                    <Option value="10">October</Option>
                                    <Option value="11">November</Option>
                                    <Option value="12">December</Option>
                                    </>
                                )}
                            </Select>
                        </Form.Item>
                    )}
                    {showHOVField && (
                        <Form.Item
                            label={`HOV (for month ${month})`}
                            name="hov"
                            rules={[{ required: true, message: 'Please enter HOV' }]}
                        >
                            <Input type='number' placeholder="Enter HOV" onChange={handleFormChange} />
                        </Form.Item>
                    )
}
                </Form>
            </ScrollableBody>
        </Modal>
    );
};

export default UploaderFilterModal;
